import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Dialog,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSearch } from '@/lib/queries';
import { useUiStore } from '@/stores/uiStore';
import type { SearchHit } from '@/lib/types';

/**
 * Navigation shortcuts, shown before anything has been typed.
 *
 * Flagged so the row does not claim to be a task or a project: labelling
 * "Go to Timesheet" as a Task is worse than showing nothing, because it teaches
 * the reader to distrust the chip on the rows where it is real.
 */
const QUICK_ACTIONS: Array<SearchHit & { isNavigation?: true }> = [
  { type: 'project', id: 'nav-projects', title: 'Go to Projects', link: '/projects', isNavigation: true },
  { type: 'task', id: 'nav-mywork', title: 'Go to My Work', link: '/my-work', isNavigation: true },
  { type: 'task', id: 'nav-timesheet', title: 'Go to Timesheet', link: '/timesheet', isNavigation: true },
  {
    type: 'project',
    id: 'nav-command',
    title: 'Go to Command Center',
    link: '/command-center',
    isNavigation: true,
  },
];

const TYPE_LABELS: Record<SearchHit['type'], string> = {
  task: 'Task',
  project: 'Project',
  user: 'Person',
  client: 'Client',
};

export function CommandPalette() {
  const open = useUiStore((s) => s.commandPaletteOpen);
  const setOpen = useUiStore((s) => s.setCommandPalette);
  const navigate = useNavigate();

  const [term, setTerm] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const { data: hits, isFetching } = useSearch(term);

  const results = useMemo<SearchHit[]>(() => {
    if (term.trim().length < 2) return QUICK_ACTIONS;
    return hits ?? [];
  }, [term, hits]);

  useEffect(() => {
    setHighlighted(0);
  }, [term, results.length]);

  // ⌘K / Ctrl+K toggles the palette from anywhere in the app.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(!useUiStore.getState().commandPaletteOpen);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setOpen]);

  const close = () => {
    setOpen(false);
    setTerm('');
  };

  const choose = (hit: SearchHit) => {
    navigate(hit.link);
    close();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((i) => Math.min(i + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && results[highlighted]) {
      event.preventDefault();
      choose(results[highlighted]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(3px)' } } }}
      // A negative margin pulled the panel above the viewport, clipping the
      // first results out of reach. Anchored below the top edge instead, and
      // capped so a long result list scrolls inside the panel rather than
      // growing past the screen.
      PaperProps={{
        sx: {
          borderRadius: 3,
          alignSelf: 'flex-start',
          mt: { xs: 2, sm: 8 },
          maxHeight: 'calc(100vh - 96px)',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <SearchIcon sx={{ color: 'text.disabled' }} />
        <InputBase
          autoFocus
          fullWidth
          value={term}
          placeholder="Search tasks, projects, people…"
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onKeyDown}
          sx={{ fontSize: '0.9375rem' }}
        />
        <Chip size="small" label="ESC" variant="outlined" onClick={close} />
      </Stack>

      <List sx={{ maxHeight: 380, overflowY: 'auto', p: 1 }}>
        {results.length === 0 && (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {isFetching ? 'Searching…' : `No matches for "${term}"`}
            </Typography>
          </Box>
        )}

        {results.map((hit, index) => (
          <ListItemButton
            key={`${hit.type}-${hit.id}`}
            selected={index === highlighted}
            onMouseEnter={() => setHighlighted(index)}
            onClick={() => choose(hit)}
            sx={{ borderRadius: 2 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                mr: 1.5,
                bgcolor: hit.color ?? 'primary.main',
              }}
            />
            <ListItemText
              primary={hit.title}
              secondary={hit.subtitle}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 550 }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
            <Chip
              size="small"
              label={
                (hit as { isNavigation?: boolean }).isNavigation
                  ? 'Jump to'
                  : TYPE_LABELS[hit.type]
              }
              sx={{
                bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                color: 'text.secondary',
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Dialog>
  );
}
